import { CreditCard, Plus, FileText, Image, MoreHorizontal, Zap, ThumbsUp, ThumbsDown, House, Pause, Play, User, Edit2, Trash2, Sparkles, Upload, ChevronDown } from 'lucide-react';
import { Negociacao, UnidadeConsumidora } from '../../../../types';
import { supabase } from '../../../../src/lib/supabase';
import React, { useState, useEffect } from 'react';
import UnidadeModal from '../../../../views/Financeiro/UnidadeModal';
import PlanModal from './PlanModal';
import LossReasonModal from './LossReasonModal';
import DocumentUploadModal from './DocumentUploadModal';
import EditClientModal from './EditClientModal';
import HistoricoVisitas from './HistoricoVisitas';
import LemitConsultaModal from './LemitConsultaModal';
import EnriquecimentoPanel from '../Lemit/EnriquecimentoPanel';
import RepresentanteModal from '../Lemit/RepresentanteModal';
import { getLatestLemitConsulta, getLatestLemitConsultaByNegociacao, setRepresentanteLegal, consultarLemit } from '../../../../src/services/lemit';
import { LemitPessoaData, LemitEmpresaData, LemitTipo } from '../../../../types';
import EnviarFaturaModal from '../EnviarFaturaModal';
import { mapFaturaDataToUC } from '../faturaExtraction';
import { uploadDocumentToNegotiation } from '../documentUpload';
import { logNegotiationAction } from '../../../../src/lib/negotiationLogger';
import { logUsageEvent } from '../../../../src/lib/logger';
import { useAuth } from '../../../../src/contexts/AuthContext';
import { useToast } from '../../../../src/contexts/ToastContext';
import ConfirmModal from '../../../../src/components/ConfirmModal';
import { getPlano } from '../../../../src/lib/politicaComercial';
import { recalcularCondicaoComercial } from '../../../../src/lib/politicaComercialDb';

interface DetailRightColumnProps {
    negotiation: Negociacao;
    ucs: UnidadeConsumidora[];
    onUpdate: () => void;
}

const DetailRightColumn: React.FC<DetailRightColumnProps> = ({ negotiation, ucs, onUpdate }) => {
    const { user, profile } = useAuth();
    const { addToast } = useToast();
    const [editingUC, setEditingUC] = useState<UnidadeConsumidora | null>(null);
    const [showAddUCModal, setShowAddUCModal] = useState(false);
    const [showEnviarFaturaModal, setShowEnviarFaturaModal] = useState(false);
    const [ucPrefillFromFatura, setUcPrefillFromFatura] = useState<Partial<UnidadeConsumidora> | null>(null);
    const [showPlanModal, setShowPlanModal] = useState(false);
    const [showDocumentUploadModal, setShowDocumentUploadModal] = useState(false);
    const [showEditClientModal, setShowEditClientModal] = useState(false);
    const [showLossReasonModal, setShowLossReasonModal] = useState(false);
    const [showLemitModal, setShowLemitModal] = useState(false);
    const [ucToDelete, setUcToDelete] = useState<UnidadeConsumidora | null>(null);
    const [deletingUC, setDeletingUC] = useState(false);
    // Enriquecimento Lemit já salvo (sem custo) — dados do lead + do sócio/representante
    const [enrData, setEnrData] = useState<{ data: LemitPessoaData | LemitEmpresaData; tipo: LemitTipo } | null>(null);
    const [socioData, setSocioData] = useState<LemitPessoaData | null>(null);
    const [enrOpen, setEnrOpen] = useState(false);
    const [showRepModal, setShowRepModal] = useState(false);
    const [salvandoRep, setSalvandoRep] = useState(false);

    useEffect(() => {
        let active = true;
        (async () => {
            const doc = (negotiation.cpf_cnpj || '').replace(/\D/g, '');
            let consulta = null;
            if (doc.length === 11 || doc.length === 14) {
                const tipo: LemitTipo = doc.length === 14 ? 'empresa' : 'pessoa';
                consulta = await getLatestLemitConsulta(negotiation.cpf_cnpj || '', tipo).catch(() => null);
            }
            // Legado: lead enriquecido cujo cpf_cnpj ficou vazio — acha pela negociação.
            if (!consulta) consulta = await getLatestLemitConsultaByNegociacao(negotiation.id).catch(() => null);
            if (!active) return;
            setEnrData(consulta?.resultado ? { data: consulta.resultado, tipo: consulta.tipo } : null);

            // O que a Lemit extraiu do CPF do representante (consulta separada, sem custo).
            const cpfRep = (negotiation.cpf_representante || '').replace(/\D/g, '');
            if (cpfRep.length === 11) {
                const cs = await getLatestLemitConsulta(cpfRep, 'pessoa').catch(() => null);
                if (active) setSocioData((cs?.resultado as LemitPessoaData) || null);
            } else if (active) {
                setSocioData(null);
            }
        })();
        return () => { active = false; };
    }, [negotiation.id, negotiation.cpf_cnpj, negotiation.cpf_representante]);

    const sociosLemit = ((enrData?.data as LemitEmpresaData)?.socios) || [];
    const representantePendente = enrData?.tipo === 'empresa' && sociosLemit.length > 1
        && !(negotiation.nome_representante_legal || '').trim();

    const [consultandoSocio, setConsultandoSocio] = useState(false);
    const [custoPessoa, setCustoPessoa] = useState<number | null>(null);

    useEffect(() => {
        supabase.from('system_settings').select('value').eq('key', 'lemit_custo_pessoa').maybeSingle()
            .then(({ data }) => {
                const p = parseFloat(String(data?.value ?? '').replace(',', '.'));
                if (!isNaN(p)) setCustoPessoa(p);
            });
    }, []);

    /** Consulta paga do CPF do representante — traz telefone/e-mail/endereço pessoais. */
    const consultarSocio = async () => {
        const cpf = (negotiation.cpf_representante || '').replace(/\D/g, '');
        if (cpf.length !== 11) return;
        setConsultandoSocio(true);
        try {
            // sem `segmento`: em produção ele é repassado à Lemit e causa 404 (ver consultarLemit)
            const r = await consultarLemit({ criterio: 'cpf', valor: cpf, negociacaoId: negotiation.id });
            setSocioData(r.data as LemitPessoaData);
            addToast(r.source === 'cache' ? 'Dados do dono recuperados do cache (sem custo).' : 'Dados do dono consultados na Lemit.', 'success');
        } catch (e: any) {
            addToast('Erro ao consultar o CPF do dono: ' + (e.message || ''), 'error');
        } finally {
            setConsultandoSocio(false);
        }
    };

    const confirmarRepresentante = async (socio: { nome?: string; cpf?: string }) => {
        setSalvandoRep(true);
        try {
            await setRepresentanteLegal(negotiation.id, { nome: socio.nome, cpf: socio.cpf }, user?.id);
            addToast('Representante legal definido.', 'success');
            setShowRepModal(false);
            onUpdate();
        } catch (e: any) {
            addToast('Erro ao definir representante: ' + (e.message || ''), 'error');
        } finally {
            setSalvandoRep(false);
        }
    };
    const [docToDelete, setDocToDelete] = useState<{ url: string; name: string } | null>(null);
    const [deletingDoc, setDeletingDoc] = useState(false);

    useEffect(() => {
        if (negotiation?.cliente_nome?.includes('Eduardo Barbosa') && !negotiation.pasta_sharepoint_url) {
            supabase.from('negociacoes').update({
                pasta_sharepoint_url: 'https://vigorenergy.sharepoint.com/sites/Operacoes-FS/Documentos%20Compartilhados/Forms/AllItems.aspx?id=%2Fsites%2FOperacoes%2DFS%2FDocumentos%20Compartilhados%2FNegocia%C3%A7%C3%B5es%2F111%5FEduardo%20Barbosa%20de%20Brito&viewid=66b87a35%2Dd5b7%2D4c6a%2D9b7f%2D37a7eb884733'
            }).eq('id', negotiation.id).then(() => {
                onUpdate();
            });
        }
    }, [negotiation]);

    // Logic to determine Plan from the first UC (assuming they share the plan for now, or Negotiation context)
    // In our DB, we save plan details in 'unidades_consumidoras' or potentially in negotiation if we updated it.
    // Based on previous task, we saved in UC. Let's look at the first UC.
    const primaryUC = ucs.length > 0 ? ucs[0] : null;
    const hasPlan = primaryUC && (primaryUC.desconto_tarifa || primaryUC.desconto_bandeira);

    // Nova Política Comercial: volume mudou (UC incluída/editada/removida) →
    // reclassifica o público e recalcula o multiplicador, alertando o vendedor.
    const recalcularAposMudancaDeUC = async () => {
        try {
            const res = await recalcularCondicaoComercial(negotiation.id);
            if (res?.mudou) {
                addToast(
                    `Volume da negociação mudou (${res.volumeTotal.toLocaleString('pt-BR')} kWh): público reclassificado para ${res.atual.publico} e multiplicador recalculado para ${res.atual.final.toFixed(2).replace('.', ',')}x.`,
                    'info'
                );
            }
        } catch (err) {
            console.error('Erro ao reclassificar condição comercial:', err);
        }
    };

    const isPausado = negotiation.status === 'em_pausa';
    const isPerdido = negotiation.status === 'perdido';
    const isCriador = !!user?.id && user.id === negotiation.responsavel_id;

    const handleStatusChange = async (newStatus: string) => {
        await supabase
            .from('negociacoes')
            .update({ status: newStatus })
            .eq('id', negotiation.id);

        await logNegotiationAction(negotiation.id, user?.id, `marcou como ${newStatus}`, { from: negotiation.status, to: newStatus });
        logUsageEvent({ userId: user?.id, modulo: 'crm', funcionalidade: 'funil', acao: 'mudar_status_negociacao', origem: 'acao' });

        onUpdate();
    };

    const handleRetomarPausa = () => {
        if (!isCriador) {
            addToast('Apenas o criador da negociação pode retomá-la.', 'warning');
            return;
        }
        handleStatusChange('em_andamento');
    };

    const handleReativarPerdido = async () => {
        if (!isCriador) {
            addToast('Apenas o criador da negociação pode reativá-la.', 'warning');
            return;
        }

        await supabase
            .from('negociacoes')
            .update({ status: 'em_andamento', motivo_perda: null, observacoes_perda: null })
            .eq('id', negotiation.id);

        await logNegotiationAction(negotiation.id, user?.id, 'reativou a negociação', { from: negotiation.status, to: 'em_andamento' });
        logUsageEvent({ userId: user?.id, modulo: 'crm', funcionalidade: 'funil', acao: 'reativar_negociacao', origem: 'acao' });

        onUpdate();
    };

    const handleDeleteUC = async () => {
        if (!ucToDelete) return;

        setDeletingUC(true);
        try {
            const { error } = await supabase
                .from('unidades_consumidoras')
                .delete()
                .eq('id', ucToDelete.id);

            if (error) throw error;

            const { data: remainingUCs, error: remainingError } = await supabase
                .from('unidades_consumidoras')
                .select('numero_uc')
                .eq('negociacao_id', negotiation.id)
                .order('created_at', { ascending: true });

            if (remainingError) throw remainingError;

            const nextPrimaryUC = remainingUCs?.[0]?.numero_uc || null;

            const { error: negotiationError } = await supabase
                .from('negociacoes')
                .update({ numero_uc: nextPrimaryUC })
                .eq('id', negotiation.id);

            if (negotiationError) throw negotiationError;

            await logNegotiationAction(negotiation.id, user?.id, 'excluiu uma UC', {
                uc_id: ucToDelete.id,
                numero_uc: ucToDelete.numero_uc,
                next_primary_uc: nextPrimaryUC,
            });
            logUsageEvent({ userId: user?.id, modulo: 'crm', funcionalidade: 'funil', acao: 'excluir_uc_negociacao', origem: 'acao' });

            addToast(`UC ${ucToDelete.numero_uc} excluída com sucesso.`, 'success');
            setUcToDelete(null);
            await recalcularAposMudancaDeUC();
            onUpdate();
        } catch (error: any) {
            console.error('Error deleting UC:', error);
            addToast(`Erro ao excluir UC: ${error.message || 'erro desconhecido'}`, 'error');
        } finally {
            setDeletingUC(false);
        }
    };

    const handleDeleteDocument = async () => {
        if (!docToDelete) return;

        setDeletingDoc(true);
        try {
            const currentDocs = Array.isArray(negotiation.documentos_json) ? negotiation.documentos_json : [];
            const nextDocs = currentDocs.filter((doc) => !(doc?.url === docToDelete.url && doc?.name === docToDelete.name));

            const { error } = await supabase
                .from('negociacoes')
                .update({ documentos_json: nextDocs })
                .eq('id', negotiation.id);

            if (error) throw error;

            await logNegotiationAction(negotiation.id, user?.id, 'excluiu um documento anexado', {
                document_name: docToDelete.name,
                document_url: docToDelete.url,
            });
            logUsageEvent({ userId: user?.id, modulo: 'crm', funcionalidade: 'funil', acao: 'excluir_documento_negociacao', origem: 'acao' });

            addToast('Documento removido com sucesso.', 'success');
            setDocToDelete(null);
            onUpdate();
        } catch (error: any) {
            console.error('Error deleting document:', error);
            addToast(`Erro ao remover documento: ${error.message || 'erro desconhecido'}`, 'error');
        } finally {
            setDeletingDoc(false);
        }
    };

    return (
        <div className="space-y-6">

            {/* Action Buttons */}
            <div className="flex items-center gap-2">
                <button
                    onClick={() => isPausado ? handleRetomarPausa() : handleStatusChange('em_pausa')}
                    disabled={isPausado && !isCriador}
                    title={isPausado && !isCriador ? 'Apenas o criador da negociação pode retomá-la.' : undefined}
                    className={`flex-1 px-4 py-2.5 rounded-xl border flex items-center justify-center gap-2 text-sm font-bold transition-all shadow-sm
                        ${isPausado ? 'bg-yellow-100 dark:bg-yellow-900/30 border-yellow-200 text-yellow-700 dark:text-yellow-400' : 'bg-white dark:bg-slate-900 border-yellow-200 text-gray-600 dark:text-slate-300 hover:bg-yellow-50 dark:hover:bg-yellow-900/40 hover:text-yellow-700 dark:text-yellow-400 hover:border-yellow-300'}
                        ${isPausado && !isCriador ? 'opacity-60 cursor-not-allowed' : ''}
                    `}
                >
                    {isPausado ? 'Retomar negociação' : 'Pausar negociação'} {isPausado ? <Play size={16} /> : <Pause size={16} />}
                </button>
                <button
                    onClick={() => isPerdido ? handleReativarPerdido() : setShowLossReasonModal(true)}
                    disabled={isPerdido && !isCriador}
                    title={isPerdido && !isCriador ? 'Apenas o criador da negociação pode reativá-la.' : undefined}
                    className={`flex-1 px-4 py-2.5 rounded-xl border flex items-center justify-center gap-2 text-sm font-bold transition-all shadow-sm
                        ${isPerdido ? 'bg-red-100 dark:bg-red-900/30 border-red-200 dark:border-red-800/50 text-red-700 dark:text-red-400' : 'bg-white dark:bg-slate-900 border-red-200 dark:border-red-800/50 text-gray-600 dark:text-slate-300 hover:bg-red-50 dark:hover:bg-red-900/40 dark:bg-red-900/20 hover:text-red-700 dark:text-red-400 hover:border-red-300'}
                        ${isPerdido && !isCriador ? 'opacity-60 cursor-not-allowed' : ''}
                    `}
                >
                    {isPerdido ? 'Reativar negociação' : 'Perdido'} {isPerdido ? <ThumbsUp size={16} /> : <ThumbsDown size={16} />}
                </button>
            </div>

            {/* Widget A0: Dados do Cliente */}
            <div className="bg-white dark:bg-slate-900 rounded-xl border border-gray-200 dark:border-slate-700 p-4 shadow-sm">
                <div className="flex items-center justify-between mb-3 text-gray-400 dark:text-slate-500">
                    <div className="flex items-center gap-2">
                        <User size={16} />
                        <span className="text-xs font-bold uppercase tracking-wider text-gray-500 dark:text-slate-400">Dados do Cliente</span>
                    </div>
                    <button
                        onClick={() => setShowEditClientModal(true)}
                        className="text-primary hover:bg-primary/10 p-1.5 rounded-lg transition-colors"
                        title="Editar dados do cliente"
                    >
                        <Edit2 size={14} />
                    </button>
                </div>

                <div className="space-y-2">
                    <div className="text-xs text-gray-600 dark:text-slate-300 space-y-1 mt-1">
                        <p>{negotiation.tipo_cliente === 'PJ' ? 'Razão Social' : 'Nome'}: <span className="font-semibold text-gray-800 dark:text-slate-100">{negotiation.cliente_nome}</span></p>
                        <p>{negotiation.tipo_cliente === 'PJ' ? 'CNPJ' : 'CPF'}: <span className="font-semibold text-gray-800 dark:text-slate-100">{negotiation.cpf_cnpj || '-'}</span></p>
                        {negotiation.tipo_cliente === 'PJ' && (
                            <>
                                <p>Representante Legal: <span className="font-semibold text-gray-800 dark:text-slate-100">{negotiation.nome_representante_legal || '-'}</span></p>
                                <p>CPF Representante: <span className="font-semibold text-gray-800 dark:text-slate-100">{negotiation.cpf_representante || '-'}</span></p>
                            </>
                        )}
                        <p>E-mail: <span className="font-semibold text-gray-800 dark:text-slate-100">{negotiation.email || '-'}</span></p>
                        <p>WhatsApp: <span className="font-semibold text-gray-800 dark:text-slate-100">{negotiation.whatsapp || '-'}</span></p>
                    </div>
                </div>

                {/* Enriquecimento Lemit — somente gerentes/admins */}
                {(profile?.role === 'adm' || profile?.role === 'gerente') && (
                    <button
                        onClick={() => setShowLemitModal(true)}
                        title="Buscar dados de prospecção na Lemit (CPF/CNPJ, nome ou e-mail)"
                        className="mt-3 w-full flex items-center justify-center gap-2 px-3 py-2.5 rounded-xl text-xs font-bold text-white bg-gradient-to-r from-[#5F6C37] to-[#6d7c3f] hover:from-[#4d5a2c] hover:to-[#5F6C37] shadow-sm hover:shadow-md hover:-translate-y-[1px] transition-all"
                    >
                        <Sparkles size={14} />
                        Enriquecer dados (Lemit)
                    </button>
                )}
            </div>

            {/* Widget A0.1: Enriquecimento Lemit (dados do lead + do sócio/dono) */}
            {enrData && (
                <div className="bg-white dark:bg-slate-900 rounded-xl border border-gray-200 dark:border-slate-700 shadow-sm overflow-hidden">
                    <button onClick={() => setEnrOpen((v) => !v)} className="w-full flex items-center gap-2 px-4 py-3 text-left">
                        <Sparkles size={16} className="text-[#5F6C37]" />
                        <span className="text-xs font-bold uppercase tracking-wider text-gray-500 dark:text-slate-400">Enriquecimento Lemit</span>
                        <span className="ml-1 px-1.5 py-0.5 text-[9px] font-bold rounded bg-violet-100 text-violet-700 dark:bg-violet-900/30 dark:text-violet-400">
                            {enrData.tipo === 'empresa' ? 'empresa + sócio' : 'pessoa'}
                        </span>
                        {representantePendente && (
                            <span className="px-1.5 py-0.5 text-[9px] font-bold rounded bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400">
                                definir representante
                            </span>
                        )}
                        <ChevronDown size={16} className={`ml-auto text-gray-400 transition-transform ${enrOpen ? 'rotate-180' : ''}`} />
                    </button>
                    {enrOpen && (
                        <div className="px-3 pb-3">
                            <EnriquecimentoPanel
                                data={enrData.data}
                                tipo={enrData.tipo}
                                socioData={socioData}
                                representantePendente={representantePendente}
                                onEscolherRepresentante={sociosLemit.length > 1 ? () => setShowRepModal(true) : undefined}
                                onConsultarSocio={consultarSocio}
                                consultandoSocio={consultandoSocio}
                                custoPessoa={custoPessoa}
                                highlight={{
                                    socioPrincipalCpf: (negotiation.cpf_representante || '').replace(/\D/g, '') || undefined,
                                    representante: negotiation.nome_representante_legal || negotiation.cpf_representante
                                        ? { nome: negotiation.nome_representante_legal || undefined, cpf: negotiation.cpf_representante || undefined }
                                        : undefined,
                                }}
                            />
                        </div>
                    )}
                    <RepresentanteModal
                        isOpen={showRepModal}
                        onClose={() => setShowRepModal(false)}
                        socios={sociosLemit}
                        empresaNome={negotiation.cliente_nome}
                        cpfAtual={negotiation.cpf_representante}
                        salvando={salvandoRep}
                        onConfirm={confirmarRepresentante}
                    />
                </div>
            )}

            {/* Widget A: Plano */}
            <div className="bg-white dark:bg-slate-900 rounded-xl border border-gray-200 dark:border-slate-700 p-4 shadow-sm">
                <div className="flex items-center gap-2 mb-3 text-gray-400 dark:text-slate-500">
                    <CreditCard size={16} />
                    <span className="text-xs font-bold uppercase tracking-wider text-gray-500 dark:text-slate-400">Plano</span>
                </div>

                {hasPlan ? (
                    <div className="space-y-2">
                        <div className="text-xs text-gray-600 dark:text-slate-300 space-y-1 mt-1">
                            {negotiation.plano_codigo && (
                                <p>Plano: <span className="font-semibold text-gray-800 dark:text-slate-100">{getPlano(negotiation.plano_codigo)?.label || negotiation.plano_codigo}</span></p>
                            )}
                            <p>Desconto da tarifa: <span className="font-semibold text-gray-800 dark:text-slate-100">{Math.round((primaryUC?.desconto_tarifa || 0) * 100)}%</span></p>
                            <p>Desconto da bandeira: <span className="font-semibold text-gray-800 dark:text-slate-100">{Math.round((primaryUC?.desconto_bandeira || 0) * 100)}%</span></p>
                            {primaryUC?.carencia && <p>Carência: <span className="font-semibold text-gray-800 dark:text-slate-100">{primaryUC.carencia}</span></p>}
                            <p>Fidelidade: <span className="font-semibold text-gray-800 dark:text-slate-100">{negotiation.fidelidade || 'Sem fidelidade'}</span></p>
                            {negotiation.publico && negotiation.multiplicador_final != null && (
                                <p>Público / multiplicador: <span className="font-semibold text-primary">Público {negotiation.publico} · {Number(negotiation.multiplicador_final).toFixed(2).replace('.', ',')}x</span></p>
                            )}
                            {(profile?.role === 'adm' || profile?.role === 'gerente') && negotiation.desconto_adicional && (
                                <p>Desconto adicional: <span className="font-semibold text-gray-800 dark:text-slate-100">{negotiation.desconto_adicional}</span></p>
                            )}
                        </div>
                    </div>
                ) : (
                    <p className="text-xs text-gray-400 dark:text-slate-500 py-2">Nenhum plano configurado.</p>
                )}

                <button
                    onClick={() => setShowPlanModal(true)}
                    className="w-full mt-3 py-2 bg-gray-50 dark:bg-slate-800 text-gray-600 dark:text-slate-300 rounded-lg text-xs font-bold border border-gray-200 dark:border-slate-700 hover:bg-gray-100 dark:hover:bg-slate-700 dark:bg-slate-800/80 transition-colors"
                >
                    Alterar plano
                </button>
            </div>

            <PlanModal
                isOpen={showPlanModal}
                onClose={() => setShowPlanModal(false)}
                ucs={ucs}
                negotiation={negotiation}
                onSave={onUpdate}
            />

            {/* Widget B: Unidades Consumidoras */}
            <div className="bg-white dark:bg-slate-900 rounded-xl border border-gray-200 dark:border-slate-700 p-4 shadow-sm">
                <div className="flex items-center gap-2 mb-3 text-gray-400 dark:text-slate-500">
                    <House size={16} />
                    <span className="text-xs font-bold uppercase tracking-wider text-gray-500 dark:text-slate-400">Unidades consumidoras ({ucs.length})</span>
                </div>

                <div className="space-y-3">
                    {ucs.map(uc => (
                        <div
                            key={uc.id}
                            onClick={() => setEditingUC(uc)}
                            className="relative p-3 bg-gray-50 dark:bg-slate-800 rounded-lg border border-gray-100 dark:border-slate-700/50 group hover:border-primary/30 hover:shadow-md transition-all cursor-pointer"
                        >
                            <div className="mb-1 font-bold text-sm text-gray-800 dark:text-slate-100 flex justify-between items-center gap-2">
                                <span>UC: {uc.numero_uc}</span>
                                <button
                                    type="button"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        setUcToDelete(uc);
                                    }}
                                    className="opacity-100 lg:opacity-0 lg:group-hover:opacity-100 p-1.5 rounded-lg text-red-500 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 transition-all"
                                    title="Excluir UC da negociação"
                                >
                                    <Trash2 size={14} />
                                </button>
                            </div>
                            <div className="text-xs text-gray-500 dark:text-slate-400 space-y-1 mt-2">
                                <div className="grid grid-cols-2 gap-x-2 gap-y-1">
                                    <p>Ligação: <span className="font-semibold text-gray-700 dark:text-slate-200">{uc.tipo_ligacao || '-'}</span></p>
                                    <p>Grupo: <span className="font-semibold text-gray-700 dark:text-slate-200">{uc.grupo_tarifario || '-'}</span></p>
                                    <p className="col-span-2">Concessionária: <span className="font-semibold text-gray-700 dark:text-slate-200">{uc.concessionaria || '-'}</span></p>
                                </div>

                                {uc.consumo_medio_kwh ? (
                                    <p className="flex items-center gap-1 mt-2 text-primary bg-primary/5 dark:bg-primary/10 w-fit px-1.5 py-0.5 rounded font-semibold">
                                        <Zap size={10} /> {uc.consumo_medio_kwh} kWh
                                    </p>
                                ) : null}
                            </div>
                        </div>
                    ))}
                </div>

                <div className="flex items-center gap-2 mt-3">
                    <button
                        onClick={() => {
                            setUcPrefillFromFatura(null);
                            setShowAddUCModal(true);
                        }}
                        className="flex-1 py-2 bg-white dark:bg-slate-900 text-gray-600 dark:text-slate-300 rounded-lg text-xs font-bold border border-gray-200 dark:border-slate-700 hover:bg-gray-50 dark:hover:bg-slate-800/80 dark:bg-slate-800 transition-colors flex items-center justify-center gap-2 shadow-sm"
                    >
                        <Plus size={14} /> Adicionar UC
                    </button>
                    <button
                        onClick={() => setShowEnviarFaturaModal(true)}
                        title="Preencher os dados da UC a partir de uma fatura em PDF"
                        className="flex-1 py-2 bg-primary/5 dark:bg-primary/10 text-primary rounded-lg text-xs font-bold border border-primary/10 dark:border-primary/20 hover:bg-primary/10 dark:bg-primary/20 transition-colors flex items-center justify-center gap-2 shadow-sm"
                    >
                        <Upload size={14} /> Enviar fatura (PDF)
                    </button>
                </div>
            </div>

            <UnidadeModal
                isOpen={!!editingUC || showAddUCModal}
                onClose={() => {
                    setEditingUC(null);
                    setShowAddUCModal(false);
                    setUcPrefillFromFatura(null);
                }}
                unidade={editingUC}
                initialNegotiation={{ id: negotiation.id, cliente_nome: negotiation.cliente_nome }}
                prefill={ucPrefillFromFatura}
                onSave={() => {
                    recalcularAposMudancaDeUC().finally(onUpdate);
                }}
            />

            <EnviarFaturaModal
                isOpen={showEnviarFaturaModal}
                onClose={() => setShowEnviarFaturaModal(false)}
                onExtracted={(data, file) => {
                    setShowEnviarFaturaModal(false);
                    setUcPrefillFromFatura(mapFaturaDataToUC(data) as Partial<UnidadeConsumidora>);
                    setShowAddUCModal(true);

                    const nomeDocumento = data.numero_uc ? `Fatura - UC ${data.numero_uc}` : file.name.replace(/\.pdf$/i, '');
                    uploadDocumentToNegotiation(negotiation.id, file, nomeDocumento).then(result => {
                        if (result.ok) {
                            onUpdate();
                        } else {
                            console.error('[Enviar Fatura] Falha ao anexar PDF aos documentos:', result.error);
                            addToast(result.error || 'Fatura processada, mas não foi possível anexá-la aos documentos.', 'error');
                        }
                    });
                }}
            />

            <ConfirmModal
                isOpen={!!ucToDelete}
                onClose={() => {
                    if (!deletingUC) setUcToDelete(null);
                }}
                onConfirm={handleDeleteUC}
                title="Excluir UC da negociação"
                message={`Tem certeza que deseja excluir a UC ${ucToDelete?.numero_uc || ''} desta negociação? Essa ação remove apenas o vínculo desta UC com a negociação.`}
                confirmLabel="Sim, excluir"
                cancelLabel="Cancelar"
                variant="danger"
                loading={deletingUC}
            />

            <ConfirmModal
                isOpen={!!docToDelete}
                onClose={() => {
                    if (!deletingDoc) setDocToDelete(null);
                }}
                onConfirm={handleDeleteDocument}
                title="Excluir documento"
                message={`Tem certeza que deseja excluir o documento ${docToDelete?.name || ''}?`}
                confirmLabel="Sim, excluir"
                cancelLabel="Cancelar"
                variant="danger"
                loading={deletingDoc}
            />

            {/* Histórico de Visitas (Rotas v2) — só aparece se o lead tiver visitas */}
            <HistoricoVisitas negotiationId={negotiation.id} />

            {/* Widget C: Geradora (Placeholder based on image, though not strictly requested to function yet) */}
            <div className="bg-white dark:bg-slate-900 rounded-xl border border-gray-200 dark:border-slate-700 p-4 shadow-sm opacity-60">
                <div className="flex items-center gap-2 mb-3 text-gray-400 dark:text-slate-500">
                    <Zap size={16} />
                    <span className="text-xs font-bold uppercase tracking-wider text-gray-500 dark:text-slate-400">Geradora (0)</span>
                </div>
                <button className="w-full mt-1 py-2 bg-white dark:bg-slate-900 text-gray-600 dark:text-slate-300 rounded-lg text-xs font-bold border border-gray-200 dark:border-slate-700 transition-colors flex items-center justify-center gap-2 shadow-sm cursor-not-allowed">
                    <Plus size={14} /> Adicionar geradora
                </button>
            </div>

            {/* Widget D: Anexos e Documentos */}
            <div className="bg-white dark:bg-slate-900 rounded-xl border border-gray-200 dark:border-slate-700 p-4 shadow-sm">
                <div className="flex items-center justify-between mb-3 text-gray-400 dark:text-slate-500">
                    <div className="flex items-center gap-2">
                        <FileText size={16} />
                        <span className="text-xs font-bold uppercase tracking-wider text-gray-500 dark:text-slate-400">Documentos</span>
                    </div>
                </div>

                <div className="mb-4">
                    <button
                        onClick={() => setShowDocumentUploadModal(true)}
                        className="w-full py-2 bg-primary/5 dark:bg-primary/10 text-primary rounded-lg text-xs font-bold border border-primary/10 dark:border-primary/20 hover:bg-primary/10 dark:bg-primary/20 transition-colors flex items-center justify-center gap-2 mb-3"
                    >
                        <Plus size={14} /> Adicionar documentos
                    </button>
                </div>

                {Array.isArray(negotiation.documentos_json) && negotiation.documentos_json.some(doc => doc?.url && doc?.name) ? (
                    <div className="space-y-2">
                        {negotiation.documentos_json
                            .filter((doc): doc is { url: string; name: string } => Boolean(doc?.url && doc?.name))
                            .map((doc, index) => (
                                <div
                                    key={index}
                                    className="flex items-center gap-3 p-2.5 bg-gray-50 dark:bg-slate-800 rounded-lg border border-gray-100 dark:border-slate-700/50 hover:bg-gray-100 dark:hover:bg-slate-700 dark:bg-slate-800/80 hover:border-gray-300 dark:hover:border-slate-600 dark:border-slate-600 transition-all group"
                                >
                                    <a
                                        href={doc.url}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="flex items-center gap-3 flex-1 min-w-0"
                                    >
                                        <div className="p-1.5 bg-white dark:bg-slate-900 rounded border border-gray-200 dark:border-slate-700 text-primary group-hover:border-primary/30">
                                            <FileText size={14} />
                                        </div>
                                        <div className="flex-1 overflow-hidden">
                                            <p className="text-xs font-semibold text-gray-700 dark:text-slate-200 truncate uppercase group-hover:text-primary transition-colors">
                                                {doc.name.replace(/_/g, ' ')}
                                            </p>
                                        </div>
                                    </a>
                                    <button
                                        type="button"
                                        onClick={() => setDocToDelete({ url: doc.url, name: doc.name })}
                                        className="opacity-100 lg:opacity-0 lg:group-hover:opacity-100 p-1.5 rounded-lg text-red-500 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 transition-all"
                                        title="Excluir documento"
                                    >
                                        <Trash2 size={14} />
                                    </button>
                                </div>
                            ))}
                    </div>
                ) : (
                    <div className="py-4 text-center border border-dashed border-gray-200 dark:border-slate-700 rounded-lg bg-gray-50 dark:bg-slate-800/50 dark:bg-slate-800/50">
                        <p className="text-xs text-gray-400 dark:text-slate-500">Nenhum documento anexado.</p>
                    </div>
                )}

                {/* SharePoint Folder Link */}
                {negotiation.pasta_sharepoint_url && (
                    <div className="mt-4 pt-3 border-t border-gray-100 dark:border-slate-700/50">
                        <a
                            href={negotiation.pasta_sharepoint_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="w-full flex items-center justify-center gap-2 py-2.5 bg-orange-50 dark:bg-orange-900/20 text-orange-700 dark:text-orange-400 rounded-lg text-xs font-bold border border-orange-100 dark:border-orange-800/50 hover:bg-orange-100 dark:hover:bg-orange-900/40 hover:border-orange-300 transition-all"
                        >
                            <House size={14} />
                            Acessar Pasta SharePoint
                        </a>
                    </div>
                )}
            </div>

            <DocumentUploadModal
                isOpen={showDocumentUploadModal}
                onClose={() => setShowDocumentUploadModal(false)}
                negotiationId={negotiation.id.toString()}
                onSuccess={onUpdate}
            />

            <EditClientModal
                isOpen={showEditClientModal}
                onClose={() => setShowEditClientModal(false)}
                negotiation={negotiation}
                onSave={onUpdate}
            />

            <LossReasonModal
                isOpen={showLossReasonModal}
                onClose={() => setShowLossReasonModal(false)}
                negotiationId={negotiation.id}
                userId={user?.id}
                onConfirm={onUpdate}
            />

            {(profile?.role === 'adm' || profile?.role === 'gerente') && (
                <LemitConsultaModal
                    isOpen={showLemitModal}
                    onClose={() => setShowLemitModal(false)}
                    negotiation={negotiation}
                    onUpdate={onUpdate}
                />
            )}

        </div>
    );
};

export default DetailRightColumn;
